import { useCallback, useMemo } from "react";
import { addDays, addMonths, addWeeks, parseISO } from "date-fns";
import {
  useWidgetRegistry,
  type WidgetConfig,
  type WidgetInstance,
} from "@salestrak/ui";
import { parseFieldGroupToken, type FieldDateGrain } from "@salestrak/widgets";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import type { DatePresetValue } from "#app/lib/utils/date-presets";
import type { SortDirection } from "#app/lib/data";
import { combineWhereConditions } from "#app/lib/query-utils";
import { useWidgetFilters } from "./widget-filters";
import { useScopedWhere } from "./scope/use-scoped-where";
import type { WidgetDataSlice } from "./widget-drilldown";
import { UNASSIGNED_DIMENSION_KEY, isFieldDimension, type WidgetDimension } from "./use-dimension-counts";
import { at } from "#app/lib/utils/at";

/** Answers a custom field's type by id — the one fact a field slice's predicate
 *  needs that the slice itself does not carry. */
export type FieldTypeLookup = (fieldId: string) => { type: string } | undefined;

const GRAIN_STEP: Record<FieldDateGrain, (date: Date, amount: number) => Date> = {
  day: addDays,
  week: addWeeks,
  month: addMonths,
};

/**
 * The server predicate for a clicked custom-field bucket, under the same
 * `values.<fieldId>` filter vocabulary the advanced filter panel emits. A period
 * bucket (date grain) is the half-open range from its start to the next
 * period's; a checkbox bucket is its boolean; every other stored value matches
 * by `hasSome` (array-valued fields overlap, scalars equal-any-of). Without a
 * type to consult the drill stays closed rather than guessing the shape.
 */
function fieldDimensionWhere(
  dimension: WidgetDimension,
  ids: string[],
  fieldOf: FieldTypeLookup | undefined,
): Record<string, unknown> | null {
  const token = parseFieldGroupToken(dimension);
  const field = token && fieldOf?.(token.fieldId);
  if (!token || !field) return null;
  const { fieldId, grain } = token;
  if (grain) {
    const ranges = ids.flatMap((id) => {
      const start = parseISO(id);
      if (Number.isNaN(start.getTime())) return [];
      const end = GRAIN_STEP[grain](start, 1);
      return [{ values: { [fieldId]: { gte: start.toISOString(), lt: end.toISOString() } } }];
    });
    if (ranges.length === 0) return null;
    return ranges.length === 1 ? at(ranges, 0, "range") : { OR: ranges };
  }
  if (field.type === "checkbox") {
    const flags = ids.map((id) => id === "true");
    return { values: { [fieldId]: flags.length === 1 ? { eq: flags[0] } : { hasSome: flags } } };
  }
  return { values: { [fieldId]: { hasSome: ids } } };
}

/**
 * The predicate for the kept no-value slice (`UNASSIGNED_DIMENSION_KEY`): the
 * projects with NO row for the dimension. The issue filter only tests
 * emptiness on the assignment sets (`assignees.entityId.isEmpty`,
 * `teamIds.isEmpty`) — every other column has `eq`/`in` and no null test — so
 * the slice drills for assignee and team and stays closed elsewhere.
 * `unassignedDrillable` is the same fact for a card deciding whether the row
 * takes a press at all.
 */
function unassignedWhere(dimension: WidgetDimension): Record<string, unknown> | null {
  switch (dimension) {
    case "assignee":
      return { assignees: { entityId: { isEmpty: true } } };
    case "team":
      return { teamIds: { isEmpty: true } };
    default:
      return null;
  }
}
export function unassignedDrillable(dimension: WidgetDimension): boolean {
  return unassignedWhere(dimension) !== null;
}

/** Newest-first — the natural order for "what fed this widget". */
export const DRILLDOWN_ORDER_BY: Record<string, SortDirection> = { creationDate: "desc" };

/** The server predicate for a clicked dimension category — the same bucketing
 * the widget's `groupBy` counted, expressed as an issue `where`. Every
 * dimension narrows server-side, so the drill-down's paged queries read
 * exactly the clicked element's rows. `team` is the Assigned Team property. */
function dimensionWhere(
  dimension: WidgetDimension,
  ids: string[],
  fieldOf?: FieldTypeLookup,
): Record<string, unknown> | null {
  if (ids.includes(UNASSIGNED_DIMENSION_KEY)) return unassignedWhere(dimension);
  if (isFieldDimension(dimension)) return fieldDimensionWhere(dimension, ids, fieldOf);
  switch (dimension) {
    case "type":
      return { issueTypeId: { in: ids } };
    case "workflow":
      return { workflowId: { in: ids } };
    case "stage":
      return { stageId: { in: ids } };
    case "stageType":
      // A category slice carries its member stage ids — the same predicate a
      // stage slice takes, over the stages the category folded.
      return { stageId: { in: ids } };
    case "team":
      return { teamIds: { hasSome: ids } };
    case "assignee":
      return { assignees: { entityId: { hasSome: ids } } };
    case "age":
      return { age: { status: { in: ids } } };
    case "source":
      return { issueSourceId: { in: ids } };
    case "outcome":
      // An outcome bucket counts Update ACTIVITIES, not projects: over a window
      // one project can land in several outcome buckets, or in none. No issue
      // predicate reproduces that bucketing, so the drill-down stays closed
      // rather than paging rows the slice never counted.
      return null;
    default:
      return null;
  }
}

/** The server predicate for a clicked chart element — EXACT, so no client
 * re-filter exists downstream. A `dateRange` slice uses `gte`/`lt` bounds,
 * matching the chart bucket's `[startMs, endMs)` semantics precisely;
 * `-Infinity` (a cumulative point) drops the lower bound so the window's own
 * start still applies. Exported for widgets that drill inline (the stat
 * strip's expandable table) — the same predicate the Data drawer folds.
 * `fieldOf` resolves a custom-field dimension's type; a caller without one
 * gets a closed drill for field slices. */
export function sliceWhere(
  slice: WidgetDataSlice,
  fieldOf?: FieldTypeLookup,
): Record<string, unknown> | null {
  if (slice.kind === "outcomeActivity") return null;
  if (slice.kind === "issues") {
    // The ids ARE the predicate. An empty set has none — the issues service
    // ignores an empty `in` rather than matching nothing — so null here means
    // "nothing to page", and the consumer that built the slice knows its set
    // is empty (see the `issues` member of `WidgetDataSlice`).
    return slice.ids.length > 0 ? { issueId: { in: slice.ids } } : null;
  }
  const conditions: Record<string, unknown>[] = [];
  if (slice.kind === "dateRange") {
    const bounds: Record<string, string> = {};
    if (Number.isFinite(slice.startMs)) bounds.gte = new Date(slice.startMs).toISOString();
    if (Number.isFinite(slice.endMs)) bounds.lt = new Date(slice.endMs).toISOString();
    if (Object.keys(bounds).length > 0) conditions.push({ [slice.dateField]: bounds });
  }
  if (slice.dimension && slice.ids?.length) {
    const dim = dimensionWhere(slice.dimension, slice.ids, fieldOf);
    if (dim) conditions.push(dim);
  }
  if (conditions.length === 0) return null;
  return conditions.length === 1 ? at(conditions, 0, "condition") : { AND: conditions };
}

/**
 * Data layer for the widget detail dialog's Data drill-down: the ONE folded
 * `where` covering the issue rows feeding a widget, for the panel's windowed
 * paged queries — the panel reaches EVERY row in the widget's window, never a
 * capped chunk. Reproduces the widget's own data window off its resolved
 * config (defaults applied): the widget's `filters` blob folds first
 * (`useWidgetFilters` — archived-out unless shown, saved query, widget date
 * range), the date column follows `measure` ("updated" widgets filter on
 * lastUpdateDate), and a top-level `dateRange` key on the config still overrides
 * the ambient window when the blob doesn't — everything else inherits the
 * dashboard scope, exactly as the widget's own data hook folds it. A clicked
 * chart element (`slice`) AND-folds its exact server predicate. So the rows
 * ARE the widget's inputs.
 */
/** Projects with no assignee — what the metric's "unassigned" measure counts. */
const NO_ASSIGNEE_WHERE: Record<string, unknown> = { assignees: { entityId: { isEmpty: true } } };

/**
 * The predicate a widget's MEASURE carries on its own, beyond its window and
 * filters: the rows the figure counts, not just the rows it looks across. It
 * folds into every drill of the widget — a press on the figure, the header's
 * Maximize, a refresh — so the Data tab always lists what the card counted,
 * with no filter chip whose ✕ would show rows the card never counted.
 */
export function measureWhere(
  type: string,
  config: { measure?: unknown },
): Record<string, unknown> | null {
  if (type === "metric" && config.measure === "unassigned") return NO_ASSIGNEE_WHERE;
  return null;
}

export function useWidgetDrilldownWhere(
  instance: WidgetInstance,
  /** The active chart-element filter, if any — folded as an exact server predicate. */
  slice?: WidgetDataSlice | null,
): {
  where: Record<string, unknown>;
  /** Serialized `where` — flips on scope/filter/slice changes. */
  variablesKey: string;
} {
  const registry = useWidgetRegistry();
  const def = registry.get(instance.type);
  const { customFields } = useWorkspaceData();
  const fieldOf = useCallback<FieldTypeLookup>(
    (fieldId) => customFields.find((field) => field.customFieldId === fieldId),
    [customFields],
  );

  const resolved = useMemo(
    () => registry.resolveConfig(def, instance.config as WidgetConfig, instance.schemaVersion ?? 1),
    [registry, def, instance.config, instance.schemaVersion],
  );
  // The stat strip counts now-state by default (its reader passes
  // `dateField: null`); each windowed stat's slice carries its own exact
  // `[startMs, endMs)` bounds. Folding any ambient/config window here would
  // narrow a now-state drill to rows the stat never counted.
  const isStatStrip = instance.type === "stat-strip";
  // The date column mirrors each widget's own reader: an explicit `dateField`
  // wins; `measure: "updated"` widgets bucket on last update.
  const dateField = isStatStrip
    ? null
    : resolved.dateField === "lastUpdateDate" || resolved.measure === "updated"
      ? ("lastUpdateDate" as const)
      : ("creationDate" as const);
  const { filters, base } = useWidgetFilters(resolved.filters);
  const dateRange = isStatStrip
    ? undefined
    : ((filters.dateRange ?? resolved.dateRange ?? undefined) as DatePresetValue | undefined);
  const { where: scopedWhere } = useScopedWhere(base, {
    dateField,
    override: dateRange ? { dateRange } : undefined,
  });

  // The key serializes exactly the fields the slice predicate reads, so the
  // folded `where` stays referentially stable across renders (a slice object
  // is a fresh ref per click, never per render — but the guard costs nothing
  // and documents the contract).
  const sliceKey = slice
    ? JSON.stringify([
        slice.kind,
        slice.kind === "dimension" || slice.kind === "dateRange"
          ? (slice.dimension ?? null)
          : null,
        slice.kind === "dimension" || slice.kind === "dateRange" || slice.kind === "issues"
          ? (slice.ids ?? null)
          : null,
        slice.kind === "dateRange" ? [slice.dateField, slice.startMs, slice.endMs] : null,
      ])
    : "";
  const intrinsic = measureWhere(instance.type, resolved);
  const where = useMemo(() => {
    const narrowed = slice ? sliceWhere(slice, fieldOf) : null;
    const parts = [scopedWhere, intrinsic, narrowed].filter(
      (part): part is Record<string, unknown> => part !== null,
    );
    return parts.length > 1 ? (combineWhereConditions(parts) ?? scopedWhere) : scopedWhere;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sliceKey stands for the slice object contents
  }, [scopedWhere, intrinsic, sliceKey, fieldOf]);
  const variablesKey = useMemo(() => JSON.stringify(where), [where]);

  return { where, variablesKey };
}
